/**
 * Pluralization utility with CLDR-compliant rules for all supported languages
 * Supports complex plural rules for different languages
 */

// CLDR plural rules mapping for each language
// Format: [zero, one, two, few, many, other]
const PLURAL_RULES = {
  // Languages with simple plural rules (like English)
  en: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  de: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  es: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  fr: (n) => n <= 1 ? 1 : 0, // one: 0-1, other: all else
  it: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  pt: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  nl: (n) => n === 1 ? 1 : 0, // one: 1, other: all else
  
  // Polish (complex: one, few, many, other)
  pl: (n) => {
    if (n === 1) return 1; // one
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 2; // few
    if (n === 0 || (mod10 === 0) || (mod10 >= 5 && mod10 <= 9) || (mod100 >= 11 && mod100 <= 19)) return 3; // many
    return 0; // other
  },
  
  // Swedish, Norwegian, Danish (similar to English)
  sv: (n) => n === 1 ? 1 : 0,
  no: (n) => n === 1 ? 1 : 0,
  da: (n) => n === 1 ? 1 : 0,
  
  // Finnish
  fi: (n) => n === 1 ? 1 : 0,
  
  // Czech (complex: one, few, many, other)
  cs: (n) => {
    if (n === 1) return 1; // one
    if (n >= 2 && n <= 4) return 2; // few
    return 0; // other (including 0, 5+)
  },
  
  // Hungarian (similar to English)
  hu: (n) => n === 1 ? 1 : 0,
  
  // Romanian (complex: one, few, many)
  ro: (n) => {
    if (n === 1) return 1; // one
    if (n === 0 || (n % 100 >= 1 && n % 100 <= 19)) return 2; // few
    return 0; // other
  },
  
  // Greek (complex: one, other)
  el: (n) => n === 1 ? 1 : 0,
};

/**
 * Get plural form index for a given number and language
 * @param {number} count - Number to pluralize
 * @param {string} language - Language code
 * @returns {number} Plural form index (0=other, 1=one, 2=few, 3=many)
 */
export const getPluralForm = (count, language = 'en') => {
  const normalizedLang = language.toLowerCase();
  const rule = PLURAL_RULES[normalizedLang] || PLURAL_RULES.en;
  
  // Handle edge cases
  if (isNaN(count) || count < 0) {
    return 0; // other
  }
  
  return rule(Math.abs(count));
};

/**
 * Get plural form name for a given number and language
 * @param {number} count - Number to pluralize
 * @param {string} language - Language code
 * @returns {string} Plural form name ('zero', 'one', 'two', 'few', 'many', 'other')
 */
export const getPluralFormName = (count, language = 'en') => {
  const form = getPluralForm(count, language);
  const formNames = ['other', 'one', 'few', 'many'];
  return formNames[form] || 'other';
};

/**
 * Pluralize a translation with count
 * @param {string|object} translation - Translation string or object with plural forms
 * @param {number} count - Number to pluralize
 * @param {string} language - Language code
 * @returns {string} Pluralized translation
 */
export const pluralize = (translation, count, language = 'en') => {
  if (!translation) {
    return '';
  }

  // If translation is already a string, return it
  if (typeof translation === 'string') {
    return translation;
  }

  // If translation is an object with plural forms
  if (typeof translation === 'object') {
    const formName = getPluralFormName(count, language);
    
    // Try specific form first
    if (translation[formName]) {
      return translation[formName];
    }
    
    // Fallback to 'other' form
    if (translation.other) {
      return translation.other;
    }
    
    // Fallback to first available form
    const forms = Object.keys(translation);
    if (forms.length > 0) {
      return translation[forms[0]];
    }
  }

  return '';
};

/**
 * Format plural translation with count interpolation
 * Example: pluralizeWithCount({ one: "{{count}} item", other: "{{count}} items" }, 5, 'en')
 * Returns: "5 items"
 * 
 * @param {string|object} translation - Translation with plural forms
 * @param {number} count - Number to pluralize
 * @param {string} language - Language code
 * @returns {string} Formatted plural translation
 */
export const pluralizeWithCount = (translation, count, language = 'en') => {
  const pluralized = pluralize(translation, count, language);
  
  // Replace {{count}} placeholder if it exists
  return pluralized.replace(/\{\{count\}\}/g, String(count));
};























